import { CSSProperties, Fragment, PropsWithChildren, ReactNode, useRef } from 'react';
import { Box, BoxProps } from 'reflexbox';
import styled, { keyframes } from 'styled-components';

function Skeleton<T extends object, S>(element: T, Skeleton: S): T & { Skeleton: S } {
	return Object.assign(element, { Skeleton });
}

Skeleton.Repeat = function SkeletonRepeat(props: PropsWithChildren<{ count: number }>) {
	return (
		<>
			{[...new Array(props.count)].map((_, i) => (
				<Fragment key={i}>{props.children}</Fragment>
			))}
		</>
	);
};

const animate = keyframes`
	from { background-position: 0px 0px; }
	to { background-position: 400px 0px; }
`;

const BaseBox = styled(Box)<{ animated?: boolean }>`
	background: ${(props) =>
		`linear-gradient(-45deg,
			${props.theme.colors.lightBlueLight} 0%,
			${props.theme.colors.lightBlue} 10%,
			${props.theme.colors.lightBlue} 40%,
			${props.theme.colors.lightBlueLight} 50%,
			${props.theme.colors.lightBlue} 60%,
			${props.theme.colors.lightBlue} 90%,
			${props.theme.colors.lightBlueLight} 100%)`};
	background-attachment: fixed;
	background-size: 400px 400px;
	background-repeat: repeat;
	animation: ${animate} 1s linear infinite;
	border-radius: 2px;

	${(props) =>
		props.animated === false &&
		`
		background: ${props.theme.colors.lightBlueLight};
		animation: none;
		`}
`;

Skeleton.Box = function Box(
	props: PropsWithChildren<BoxProps & { animated?: boolean; style?: CSSProperties }>,
) {
	return (
		<BaseBox
			ref={(ref?: HTMLDivElement) =>
				ref?.getAnimations().forEach((animation) => (animation.startTime = 0))
			}
			{...props}
		/>
	);
};

const CustomWidthSkeletonBox = styled(Skeleton.Box).attrs((props: { $width: number }) => ({
	style: { width: `${props.$width}px` },
}))``;

Skeleton.DynamicBox = function DynamicSkeletonBox(
	props: BoxProps & { width?: number; maxWidth?: number },
) {
	const width = useRef(Math.random() * ((props.maxWidth ?? 200) - 100) + 100).current;

	return <CustomWidthSkeletonBox {...props} $width={props.width ?? width} />;
};

const EmptyContainer = styled(Box)`
	position: relative;

	${BaseBox} {
		background: ${(props) => props.theme.colors.lightBlueLight};
		animation: none;
	}
`;

Skeleton.Empty = function Empty(
	props: PropsWithChildren<BoxProps> & { contents: ReactNode; wide?: boolean },
) {
	return (
		<EmptyContainer {...props}>
			<Box opacity={0.5}>{props.children}</Box>
			<Box
				style={{
					position: 'absolute',
					left: '50%',
					top: '50%',
					transform: 'translate(-50%, -50%)',
					width: props.wide ? '100%' : undefined,
				}}
			>
				{props.contents}
			</Box>
		</EmptyContainer>
	);
};

export { Skeleton };
